import { colors } from 'app-v2/design-systems/theme'
import { COLOR } from 'app-v2/design-tokens'
import { Chevron, Light, Pin, Question } from 'app/assets/icons'
import { ResponsiveContainer, media, style } from 'app/assets/styles'
import { Link } from 'app/common/link'
import { Loader } from 'app/common/loader'
import { H3, H4, H5, SmallText, Text } from 'app/common/typography'
import {
    CancelOrderModal,
    Layout,
    ModalTutorialCollectMode,
    OrderPreparation,
    OrderStatus
} from 'app/ecommerce/common'
import React, { Component, Fragment } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import Modal from 'react-modal'
import { colors as oldColors } from 'shared/assets/styles'
import type { EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import type { CacOrder, DeviceCheck, MetaShortDTO } from 'shared/types'
import { featureCodes, orderPickUpType } from 'shared/utils'
import {
    formatTableToDisplay,
    getTableOrderValuesFromPickUpInfo
} from 'shared/utils-v2'
import {
    isOrderCancelledByUser,
    isOrderLaunched,
    isOrderPreparing,
    orderMeta
} from 'shared/utils/ecommerce'
import styled from 'styled-components'
import { Spacer } from '../../../../../app-v2/design-systems'
import { OrderDetails } from '../../components/order-details/OrderDetails'
import { OrderRecap } from '../../components/order-recap/OrderRecap'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'

const { shadow } = oldColors
const { DRIVE, TAKEAWAY, TABLESERVICE, PARKING, TABLE_ORDER } = orderPickUpType

type Props = {
    navigate: (to: string, state?: any) => void,
    fetchOrder: (id: string, token: string) => void,
    sendMeta: EventMetadata => void,
    pageMetadata: (payload: MetaShortDTO) => void,
    resetCart: () => void,
    intl: IntlShape,
    orders: any,
    deliveryOrders: any,
    resetAdyenRedirection: () => void,
    cancelOrder?: (
        id: string,
        token: string,
        deviceCheckPayload: DeviceCheck
    ) => void,
    isActiveZendesk: boolean
}

type State = {
    prepareOrderSuccess: boolean,
    showTuto: boolean,
    currentOrder: CacOrder | null,
    isLoading: boolean,
    cancelOrderModal: boolean,
    tableOrderLaunched: boolean
}

class CartConfirmation extends Component<Props, State> {
    state: State = {
        prepareOrderSuccess: false,
        showTuto: true,
        orderToken: '',
        orderId: '',
        currentOrder: null,
        isLoading: false,
        cancelOrderModal: false,
        tableOrderLaunched: false
    }

    async componentDidMount() {
        const {
            pageMetadata,
            orders,
            resetCart,
            fetchOrder,
            resetAdyenRedirection
        } = this.props
        this.setState({ isLoading: true })

        await resetAdyenRedirection()
        resetCart()

        const searchParams = new URLSearchParams(window.location?.search)
        const token = searchParams.get('token')
        const id = searchParams.get('id')

        if (!!token && !!id) {
            await fetchOrder(id, token)
        }
        const order = orders[token]

        this.setState({
            currentOrder: order,
            isLoading: false
        })

        pageMetadata({})
        if (!!this.state.currentOrder) {
            this.sendMeta()
        }
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.orders !== prevProps.orders ||
            this.props.deliveryOrders !== prevProps.deliveryOrders
        ) {
            this.setState({ isLoading: true })
            const searchParams = new URLSearchParams(window.location.search)
            const token = searchParams.get('token')
            const order = this.props.orders[token]
            !!order &&
                this.setState({
                    currentOrder: order,
                    isLoading: false
                })

            if (
                order &&
                order.pickUpType === TABLE_ORDER &&
                !this.state.tableOrderLaunched
            ) {
                this.setState({ tableOrderLaunched: true })
                this.waitForOrderStatusChange(token || '')
            }

            this.forceUpdate()
        }
    }

    waitForOrderStatusChange = async (token: string) => {
        const { waitForOrderStatusChange } = this.props
        try {
            await waitForOrderStatusChange(token)
        } catch (error) {
            console.error(
                'CartConfirmation, error in waitForOrderStatusChange:',
                error
            )
        }
    }

    backToHomeButton = () => {
        return (
            <StyledButtonMedium url={'/'}>
                <FormattedMessage id="clickandcollect.back.home">
                    {(text: string) => <H5>{text}</H5>}
                </FormattedMessage>
                <Underline />
            </StyledButtonMedium>
        )
    }

    wordings = () => {
        const { currentOrder } = this.state || {}
        const { restaurant } = currentOrder || {}
        const {
            onSiteInformation,
            pickupParkingRadius,
            parkingInformation,
            driveInformation,
            tableOrderInformation
        } = restaurant || {}
        const geolocationRequired = !!pickupParkingRadius
        let textKey = '',
            collectModeKey,
            descriptionKey,
            description

        switch (currentOrder?.pickUpType) {
            case TABLESERVICE:
                textKey = 'clickandcollect.confirmation.description.onsitetable'
                collectModeKey = 'clickandcollect.delivery.choice.onsite'
                descriptionKey = 'clickandcollect.confirmation.onsite'
                description = onSiteInformation
                break
            case TAKEAWAY:
                textKey = 'clickandcollect.confirmation.description.general'
                collectModeKey = 'clickandcollect.delivery.choice.takeaway'
                descriptionKey = 'clickandcollect.confirmation.takaway'
                break
            case PARKING:
                textKey = 'clickandcollect.confirmation.description.parking'
                collectModeKey = 'clickandcollect.delivery.choice.parking'
                descriptionKey = 'clickandcollect.confirmation.parking'
                description = parkingInformation
                if (geolocationRequired) {
                    textKey =
                        'clickandcollect.confirmation.description.parking.geoloc'
                }
                break
            case DRIVE:
                textKey = 'clickandcollect.confirmation.description.drive'
                collectModeKey = 'clickandcollect.delivery.choice.drive'
                descriptionKey = 'clickandcollect.confirmation.drive'
                description = driveInformation
                break
            case TABLE_ORDER:
                textKey = 'clickandcollect.confirmation.description.tableorder'
                collectModeKey = 'clickandcollect.delivery.choice.tableorder'
                descriptionKey = 'clickandcollect.confirmation.tableorder'
                description = tableOrderInformation
                break
            default:
                textKey = 'clickandcollect.confirmation.description.general'
                collectModeKey = 'clickandcollect.delivery.choice.onsite'
                descriptionKey = 'clickandcollect.confirmation.takaway'
                description = onSiteInformation
        }
        return { textKey, collectModeKey, descriptionKey, description }
    }

    sendMeta = () => {
        const { sendMeta } = this.props
        const { currentOrder } = this.state
        sendMeta({
            ...eventsDefinitions.ECOMM_ORDER_VALIDATED,
            event_label: currentOrder?.id
        })
        if (!currentOrder) return null
        const { context } = currentOrder && orderMeta(currentOrder)
        if (context) {
            const {
                transaction_id,
                value,
                currency,
                collect_choice,
                ...orderData
            } = context
            sendMeta({
                ...eventsDefinitions.GA4_ECOMM_ORDER_VALIDATED,
                order_id: currentOrder?.id,
                ...orderData
            })
        }
    }

    render() {
        const { intl, sendMeta, cancelOrder, isActiveZendesk } = this.props
        const {
            showTuto,
            currentOrder,
            isLoading,
            cancelOrderModal
        } = this.state

        if (!currentOrder) return null

        const {
            restaurant,
            pickUpType: collectMode,
            orderNumber = '',
            orderStatus,
            orbStatus,
            clientId
        } = currentOrder || {}

        const {
            textKey,
            collectModeKey,
            descriptionKey,
            description
        } = this.wordings()
        const collectModeInformation =
            description || intl.formatMessage({ id: descriptionKey })
        const isLaunched =
            isOrderLaunched(orderStatus, orbStatus) && collectMode !== DRIVE
        const isCancelledByUser = isOrderCancelledByUser(orderStatus)
        const isTableOrder = collectMode === TABLE_ORDER
        const searchParams = new URLSearchParams(window.location?.search)
        const id = searchParams.get('id')
        const token = searchParams.get('token')

        const routeFaq = intl.formatMessage({ id: 'routes.faq' })
        const newRouteFaq = process.env.REACT_APP_ZENDESK_URL
        return (
            <Layout
                background="plain"
                backgroundColor={colors.lightMayo}
                noFooter
                border
                cart
                closeEnabled
                currentStep={4}>
                <Loader loading={isLoading}>
                    <Main>
                        <Modal
                            isOpen={
                                showTuto && !!collectMode && !isCancelledByUser
                            }
                            shouldCloseOnOverlayClick
                            onRequestClose={() =>
                                this.setState({ showTuto: false })
                            }
                            style={style.classicModalStyle}>
                            <ModalTutorialCollectMode
                                collectMode={collectMode || ''}
                                handleClose={() =>
                                    this.setState({ showTuto: false })
                                }
                                geolocationRequired={
                                    restaurant &&
                                    !!restaurant.pickupParkingRadius
                                }
                            />
                        </Modal>

                        <StyledResponsiveContainer>
                            <Fragment>
                                {this.backToHomeButton()}
                                <Block>
                                    {isLaunched || isCancelledByUser ? (
                                        <Fragment>
                                            <FormattedMessage id="orb.card.order.light">
                                                {(text: string) => (
                                                    <SmallText>
                                                        {text}
                                                    </SmallText>
                                                )}
                                            </FormattedMessage>
                                            <StyledH3>{orderNumber}</StyledH3>
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <FormattedMessage
                                                id="clickandcollect.confirmation.subtitle"
                                                values={{
                                                    id: orderNumber.toUpperCase()
                                                }}>
                                                {(text: string) => (
                                                    <StyledH4 center>
                                                        {text}
                                                    </StyledH4>
                                                )}
                                            </FormattedMessage>
                                            <FormattedMessage
                                                id={textKey}
                                                values={{
                                                    collectMode: (
                                                        <FormattedMessage
                                                            id={collectModeKey}>
                                                            {(t: string) => (
                                                                <StyledText
                                                                    center>
                                                                    {t
                                                                        .toString()
                                                                        .toUpperCase()}
                                                                </StyledText>
                                                            )}
                                                        </FormattedMessage>
                                                    )
                                                }}>
                                                {(text: string) => (
                                                    <SmallText center>
                                                        {text}
                                                    </SmallText>
                                                )}
                                            </FormattedMessage>
                                        </Fragment>
                                    )}
                                    {isCancelledByUser && (
                                        <OrderStatus
                                            isDelivered={false}
                                            isCancelled={true}
                                            marginTop={false}
                                        />
                                    )}
                                    {collectMode !== DRIVE && (
                                        <>
                                            {!isCancelledByUser && (
                                                <OrderPreparation
                                                    order={currentOrder}
                                                    onSuccess={() =>
                                                        this.setState({
                                                            prepareOrderSuccess: true
                                                        })
                                                    }
                                                    alreadyClicked={isOrderPreparing(
                                                        orderStatus
                                                    )}
                                                    onClick={() => {
                                                        sendMeta({
                                                            ...eventsDefinitions.GA4_COMMAND_CANCELLATION,
                                                            page_template:
                                                                'Kingdom'
                                                        })
                                                        this.setState({
                                                            cancelOrderModal: true
                                                        })
                                                    }}
                                                    cancelOrder={cancelOrder}
                                                    // web: the list is not accessible without an account so behave like from the list
                                                    fromList={!clientId}
                                                    noBottomMargin
                                                />
                                            )}
                                            <CancelOrderModal
                                                id={id}
                                                token={token}
                                                cancelOrder={cancelOrder}
                                                cancelOrderModal={
                                                    cancelOrderModal
                                                }
                                                setCancelOrderModal={() =>
                                                    this.setState({
                                                        cancelOrderModal: false
                                                    })
                                                }
                                                setError={error =>
                                                    this.setState({ error })
                                                }
                                            />
                                        </>
                                    )}
                                    {!isTableOrder && !isCancelledByUser && (
                                        <Row marginTop>
                                            <StyledLight />
                                            <FormattedMessage id="clickandcollect.timeline.advise.order.ready">
                                                {(text: string) => (
                                                    <SmallText>
                                                        {text}
                                                    </SmallText>
                                                )}
                                            </FormattedMessage>
                                        </Row>
                                    )}
                                    {!!restaurant && (
                                        <Fragment>
                                            <Line />
                                            <Row>
                                                <div>
                                                    <Pin />
                                                </div>
                                                <div>
                                                    <H4>{restaurant.name}</H4>
                                                    <Text>
                                                        {restaurant.addressFull}
                                                    </Text>
                                                </div>
                                            </Row>
                                            <RestaurantCollectMode>
                                                <FormattedMessage
                                                    id={collectModeKey}>
                                                    {(text: string) => (
                                                        <H4>{text}</H4>
                                                    )}
                                                </FormattedMessage>
                                                <Text>
                                                    {collectModeInformation}
                                                </Text>
                                            </RestaurantCollectMode>
                                        </Fragment>
                                    )}
                                </Block>
                                <Block>
                                    <OrderRecap order={currentOrder} />
                                </Block>
                                {this.backToHomeButton()}
                                <Spacer />
                                <Block>
                                    <StyledFooter
                                        external={!!isActiveZendesk}
                                        url={routeFaq}
                                        onClick={e => {
                                            if (!!isActiveZendesk) {
                                                e.preventDefault()
                                                window.location.href = newRouteFaq
                                            }
                                        }}>
                                        <Question
                                            background={colors.bbqBrown}
                                        />
                                        <FormattedMessage id="clickandcollect.confirmation.footer">
                                            {(t: string) => (
                                                <StyledFooterLabel>
                                                    {t}
                                                </StyledFooterLabel>
                                            )}
                                        </FormattedMessage>
                                        <Chevron
                                            color={COLOR.PRIMARY}
                                            size={28}
                                        />
                                    </StyledFooter>
                                </Block>
                                <Spacer />
                            </Fragment>
                        </StyledResponsiveContainer>
                    </Main>
                </Loader>
            </Layout>
        )
    }
}

const Main = styled.section`
    padding-top: 20px;
    ${media.medium`
        padding-top: 60px;
    `};
`

const StyledResponsiveContainer = styled(ResponsiveContainer)`
    max-width: 710px;
    margin-left: auto;
    margin-right: auto;
`

const StyledH3 = styled(H3)`
    text-transform: uppercase;
`

const StyledH4 = styled(H4)`
    margin-bottom: 10px;
`

const Block = styled.div`
    border-radius: 12px;
    padding: 30px;
    background: white;
    margin-bottom: 17px;
`

const StyledButtonMedium = styled(Link)`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
`

const Underline = styled.div`
    width: 45%;
    height: 2px;
    background: ${COLOR.PRIMARY};
    margin-top: 3px;
`

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    ${props => props.marginTop && `margin-top: 30px;`}
`

const Line = styled.div`
    width: 100%;
    height: 1px;
    background: ${shadow};
    margin: 20px 0;
`

const MobileReturnButton = styled.div`
    ${media.medium`
        display: none;
    `};
`

const StyledText = styled(Text)`
    font-weight: bold;
`

const RestaurantCollectMode = styled.div`
    margin: 10px 0;
`

const StyledLight = styled(Light)`
    margin-right: 6px;
`

const StyledFooter = styled(Link)`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 0 -26px 0 -18px;
`
const StyledFooterLabel = styled(H5)`
    flex: 1;
    margin: 0 -4px 0 10px;
`

export const CartConfirmationComponent = injectIntl(CartConfirmation)
